/**
 * Batch 21b — Super-admin analytics dashboard API client.
 *
 * Typed wrappers around the `/admin/dashboard/*` endpoints. Response shapes
 * mirror the backend TypedDicts in `app/schemas/admin/dashboard.py`.
 */
import { apiClient } from "@/lib/apiClient";

// ── Panels ──────────────────────────────────────────────────────────────────
//
// NOTE (2026-06-08): the Stats deep-dive page was deleted (founder ruling —
// graveyard of the killed self-serve motion). Its exclusive API wrappers
// (funnel / revenue / credits / trials / health) were removed with it. The two
// wrappers that survive — fetchQuality (Operator quality heatmap) and
// fetchIntegrationsPanel (Operator integrations donut) — are still consumed by
// the Operator Console panels below.

interface HeatmapCell {
  action_type: string;
  model_used: string;
  week_start: string;
  total_count: number;
  down_count: number;
  down_rate: number | null;
  insufficient_sample: boolean;
}
interface QualityComment {
  rating_id: string;
  tenant_id: string;
  tenant_slug: string;
  action_type: string;
  model_used: string;
  comment: string;
  created_at: string;
}
export interface QualityPanelResponse {
  range: "13w";
  cells: HeatmapCell[];
  comments: QualityComment[];
  comments_next_cursor: string | null;
  kpis: {
    total_ratings_window: number;
    opted_in_tenant_count: number;
    overall_down_rate: number | null;
  };
}
export async function fetchQuality(): Promise<QualityPanelResponse> {
  const r = await apiClient.get<QualityPanelResponse>(
    "/admin/dashboard/quality",
    { params: { range: "13w" } }
  );
  return r.data;
}

export interface IntegrationsPanelResponse {
  range: "7d" | "30d" | "90d";
  granularity: "day" | "week";
  adoption: {
    bucket_start: string;
    new_integrations: number;
    cumulative: number;
  }[];
  sync_health: { status: string; count: number }[];
  error_breakdown: { entity_type: string; direction: string; count: number }[];
  kpis: {
    active_tenants: number;
    tenants_with_integration: number;
    tenants_without_integration: number;
    window_sync_success_rate: number;
  };
}
export async function fetchIntegrationsPanel(
  range: string
): Promise<IntegrationsPanelResponse> {
  const r = await apiClient.get<IntegrationsPanelResponse>(
    "/admin/dashboard/integrations",
    { params: { range } }
  );
  return r.data;
}

// ── Per-tenant overlay ──────────────────────────────────────────────────────

export interface TenantDetailOverlayResponse {
  tenant: {
    id: string;
    slug: string;
    name: string;
    status: string;
    stripe_customer_id: string | null;
    trial_ends_at: string | null;
    created_at: string;
  };
  credit_ledger: {
    created_at: string;
    action: string;
    amount: number;
  }[];
  credit_balance: number;
  action_breakdown: {
    action: string;
    call_count: number;
    credits_spent: number;
    estimated_cost_cents: number;
  }[];
  ratings: {
    rating_id: string;
    action_type: string;
    model_used: string;
    rating: "up" | "down";
    comment: string | null;
    created_at: string;
  }[];
  model_overrides: {
    action_type: string;
    model_name: string;
    reason: string;
    set_at: string;
    updated_at: string;
  }[];
  subscription: {
    subscription_id: string;
    status: string;
    plan_name: string;
    price_cents: number;
  } | null;
  estimated_cost_cents_30d: number;
  mrr_cents: number | null;
}

export async function fetchTenantDetail(
  tenantId: string
): Promise<TenantDetailOverlayResponse> {
  const r = await apiClient.get<TenantDetailOverlayResponse>(
    `/admin/dashboard/tenant/${tenantId}/detail`
  );
  return r.data;
}

// ── Batch 25 — Platform Health ──────────────────────────────────────────────

export interface UptimeBlock {
  source: "better_stack";
  status: "ok" | "unavailable";
  percent: number | null;
  sparkline: number[];
}

interface ExceptionGroup {
  sentry_issue_id: string;
  title: string;
  count: number;
  last_seen: string | null;
  deep_link: string;
}

export interface RecentExceptionsBlock {
  source: "sentry";
  status: "ok" | "unavailable";
  window_hours: number;
  groups: ExceptionGroup[];
}

export interface IncidentsBlock {
  source: "sentry+db";
  status: "ok" | "unavailable";
  incidents: unknown[];
}

export interface SecretsModeBlock {
  jwt: "single" | "dual";
  integration_encryption: "single" | "dual";
}

interface DestructiveOpEvent {
  event_type: string;
  subject_tenant_id: string | null;
  actor_user_id: string | null;
  occurred_at: string | null;
}

export interface DestructiveOpLastRun {
  prototype_migration: DestructiveOpEvent | null;
  tenant_hard_delete: DestructiveOpEvent | null;
  deletion_reversal: DestructiveOpEvent | null;
}

export interface PlatformHealthDeepLinks {
  better_stack_logs: string;
  sentry_issues: string;
  better_stack_metrics: string;
}

export interface PlatformHealthResponse {
  status_banner: "green" | "yellow" | "red";
  /** False when NO observability provider is wired (tokens unset). Drives a
   *  calm "Monitoring not configured" info state — never a false degraded
   *  alarm (HoD teardown P0-2 / founder ruling 2026-06-08). */
  monitoring_configured: boolean;
  uptime_90d: UptimeBlock;
  recent_exceptions: RecentExceptionsBlock;
  tenant_impacting_incidents: IncidentsBlock;
  secrets_mode: SecretsModeBlock;
  destructive_op_last_run: DestructiveOpLastRun;
  deep_links: PlatformHealthDeepLinks;
  generated_at: string;
  cache_ttl_seconds: number;
}

export async function fetchPlatformHealth(): Promise<PlatformHealthResponse> {
  const r = await apiClient.get<PlatformHealthResponse>(
    "/admin/platform-health"
  );
  return r.data;
}
